import findKey from 'lodash/findKey';
import { Moment } from 'moment';
import { createSelector } from 'reselect';

import { AppState, ProgramReducer } from 'src/config/appState';
import i18n from 'src/config/i18n';
import { isDefined } from 'src/shared/helpers/isDefined';

import { getCBTProgramState } from '../cbtProgram/selectors/cbtProgramSelectors';
import {
    ProgramEnum,
    ProgramsState,
    ProgramWeek,
    ProgramWeekEnum,
    ProgramWeeksProgress,
    WeekProgress,
} from '../models/programs';
import { programWeekIdByNumber, programWeekNumberById } from '../models/programsData';
import {
    AdditionalProgramState,
    GenericProgramStep,
    GenericProgramStepProps,
    GenericProgramWeek,
} from '../models/programsGeneric';
import { getPFAProgramState } from '../pfaProgram/selectors/pfaProgramSelectors';

export const programReducerById = {
    [ProgramEnum.CP]: ProgramReducer.CPProgram,
    [ProgramEnum.PFA]: ProgramReducer.PFAProgram,
    [ProgramEnum.CBT]: ProgramReducer.CBTProgram,
};

export const extractProgramId = (_state: AppState, id: ProgramEnum) => id;

export const extractWeekId = (_state: AppState, _programId: ProgramEnum, weekId: ProgramWeekEnum) =>
    weekId;

export const extractStepId = (
    _state: AppState,
    _programId: ProgramEnum,
    _weekId: ProgramWeekEnum,
    stepId: GenericProgramStep,
) => stepId;

export const getProgramsState = (state: AppState): ProgramsState => state.programs;

export const getProgramsProgress = (state: AppState) => getProgramsState(state).programsProgress;

export const isProgramsProgressFetched = (state: AppState) =>
    getProgramsState(state).programsProgressFetched;

export const getProgramWeeksProgress = createSelector(
    getProgramsProgress,
    extractProgramId,
    (programsProgress, programId): ProgramWeeksProgress | undefined =>
        programsProgress && programsProgress[programId],
);

export const getProgramSingleWeekProgress = createSelector(
    getProgramWeeksProgress,
    extractWeekId,
    (programWeeksProgress, weekId): WeekProgress | undefined =>
        programWeeksProgress && programWeeksProgress[weekId],
);

export const getPreviousWeekCompletionDate = createSelector(
    getProgramWeeksProgress,
    extractWeekId,
    (programWeeksProgress, weekId): Moment | null | undefined => {
        if (!(programWeeksProgress && programWeeksProgress[weekId])) {
            return undefined;
        }

        const previousWeekId = programWeekIdByNumber[programWeekNumberById[weekId] - 1];
        const previousWeekProgress = programWeeksProgress[previousWeekId];

        return previousWeekProgress && previousWeekProgress.completedAt;
    },
);

export const getProgramWeekCurrentStep = createSelector(
    getProgramSingleWeekProgress,
    (programWeekProgress): GenericProgramStep | null =>
        programWeekProgress ? programWeekProgress.currentStep : null,
);

export const isSingleProgramSingleWeekLocked = createSelector(
    getProgramSingleWeekProgress,
    (weekProgress): boolean => !!(weekProgress && !weekProgress.isUnlocked),
);

export const isSingleProgramSingleWeekStarted = createSelector(
    getProgramSingleWeekProgress,
    (weekProgress): boolean => !!(weekProgress && weekProgress.isStarted),
);

export const isSingleProgramSingleWeekCompleted = createSelector(
    getProgramSingleWeekProgress,
    (weekProgress): boolean => !!(weekProgress && weekProgress.isCompleted),
);

export const getCurrentProgramWeekNumber = createSelector(
    getProgramWeeksProgress,
    (weeksProgress): number => {
        const weekKey = weeksProgress && findKey(weeksProgress, week => week.isStarted);

        return (weekKey && programWeekNumberById[weekKey]) || 0;
    },
);

export const isProgramCompleted = createSelector(
    getProgramWeeksProgress,
    (weeksProgress): boolean =>
        !!(weeksProgress && !findKey(weeksProgress, week => week.isCompleted === false)),
);

export const getProgramProgressPercentage = createSelector(
    getProgramWeeksProgress,
    (weeksProgress): number => {
        const weeksProgressArray = weeksProgress && Object.values(weeksProgress);

        if (!weeksProgressArray || !weeksProgressArray.length) {
            return 0;
        }

        const finishedWeeks = weeksProgressArray.reduce(
            (finishedWeeksAmount, weekProgress) =>
                weekProgress.isCompleted ? finishedWeeksAmount + 1 : finishedWeeksAmount,
            0,
        );

        return Math.floor((finishedWeeks / weeksProgressArray.length) * 100);
    },
);

export const getProgramsSharedStepsProps = (state: AppState) =>
    getProgramsState(state).sharedStepsProps;

// TODO : Interface for selector's return type
export const getProgramState = (state: AppState, id: ProgramEnum): any =>
    state[programReducerById[id]];

export const getAdditionalPrograms = createSelector(
    getPFAProgramState,
    getCBTProgramState,
    (pfaProgram, cbtProgram): AdditionalProgramState[] => [pfaProgram, cbtProgram],
);

export const getProgramId = (state: AppState, id: ProgramEnum) => getProgramState(state, id).id;

export const getProgramTitleByProgramId = (state: AppState, id: ProgramEnum) =>
    getProgramState(state, id).title;

export const getProgramWeeksDataByProgramId = (state: AppState, id: ProgramEnum) => {
    const weeks: ProgramWeek[] = getProgramState(state, id).weeks;

    return weeks.map(week => {
        const translatedFields = Object.entries(week).reduce(
            (acc, [key, value]) =>
                typeof value === 'string' && value.includes(':')
                    ? {
                          ...acc,
                          [key]: i18n.t(value),
                      }
                    : acc,
            {},
        );

        return {
            ...week,
            ...translatedFields,
        };
    });
};

export const getSingleProgramSingleWeek = createSelector(
    getProgramWeeksDataByProgramId,
    extractWeekId,
    (weeks: GenericProgramWeek[], id): GenericProgramWeek | undefined =>
        weeks.find(week => week.id === id),
);

export const getStepIndex = createSelector(
    getSingleProgramSingleWeek,
    extractStepId,
    (week, stepId): number | undefined =>
        week && stepId && (week.steps as GenericProgramStep[]).findIndex(step => step === stepId),
);

export const isNextStepAvailable = createSelector(
    getSingleProgramSingleWeek,
    getStepIndex,
    (week, stepIndex): boolean =>
        !!(isDefined(stepIndex) && week && week.steps.length - 1 > stepIndex!),
);

export const isLastWeek = createSelector(
    getProgramWeeksDataByProgramId,
    getSingleProgramSingleWeek,
    (weeks: GenericProgramWeek[], week: GenericProgramWeek): boolean =>
        week.weekNumber + 1 === weeks.length,
);

export const getProgramWeekProgressPercentage = createSelector(
    getSingleProgramSingleWeek,
    getStepIndex,
    (week, currentStepIndex) =>
        week && currentStepIndex
            ? Math.floor((currentStepIndex / (week.steps.length - 1)) * 100)
            : 0,
);

export const getPreviousWeekData = createSelector(
    getProgramWeeksDataByProgramId,
    getSingleProgramSingleWeek,
    (weeks: GenericProgramWeek[], currentWeek): GenericProgramWeek | undefined => {
        if (!currentWeek) {
            return undefined;
        }

        const currentWeekIndex = weeks.findIndex(week => week.id === currentWeek.id);

        return weeks[currentWeekIndex > 0 ? currentWeekIndex - 1 : 0];
    },
);

export const getCurrentStepByProgramId = (state: AppState, id: ProgramEnum) =>
    getProgramState(state, id).currentStep;

export const getSingleProgramSharedStepsProps = (state: AppState, id: ProgramEnum) =>
    getProgramState(state, id).sharedStepsProps;

export const getProgramWeekStepsProps = createSelector(
    getSingleProgramSingleWeek,
    (week): GenericProgramStepProps[] | undefined => week && week.stepsProps,
);

export const getProgramWeekPossibleStepsProps = createSelector(
    getProgramsSharedStepsProps,
    getSingleProgramSharedStepsProps,
    getProgramWeekStepsProps,
    (
        sharedStepsProps,
        programSharedStepsProps,
        weekStepsProps,
    ): GenericProgramStepProps[] | undefined => {
        if (!weekStepsProps) {
            return undefined;
        }

        const stepsProps = [...sharedStepsProps, ...weekStepsProps];

        return programSharedStepsProps ? [...programSharedStepsProps, ...stepsProps] : stepsProps;
    },
);

export const getStepProps = createSelector(
    getProgramWeekPossibleStepsProps,
    extractStepId,
    (stepsProps, stepId): GenericProgramStepProps | undefined =>
        stepsProps && stepsProps.find(stepProps => stepProps.id === stepId),
);
